import {isSelectiveAiDomains} from 'common/HelperFunctions'
import {Dispatch} from 'redux'
import {REDUX_SET_MUST_CREATE_PASSWORD} from 'setup/redux/Actions/SignIn/ActionTypes/ReduxActionTypes'

const STORAGE_KEY = 'must_create_password'

export const SELECTIVE_AI_DASHBOARD_ENTRY = '/dashboard?step=0'

/**
 * Login/handoff response fields that decide whether the user is held on
 * /create-password before entering the app.
 *
 * - `mustChangePassword`: system-generated password (random password
 *   provisioning / admin regenerate) not yet replaced. Every domain.
 * - `isDefaultPassword`: legacy default password. Only enforced on selective
 *   AI domains — on classic tenants '123456' is still a normal login.
 */
export type PasswordGateLoginResult = {
  isDefaultPassword?: boolean
  mustChangePassword?: boolean
}

/** Where to send the user after a successful selective-AI session handoff. */
export function resolveSelectiveAiHandoffDestination(pathname: string): {
  pathname: string
  search: string
} {
  if (!pathname || pathname === '/' || pathname === '/create-password') {
    return {pathname: '/dashboard', search: '?step=0'}
  }
  return {pathname, search: ''}
}

/** True when this login/handoff result must land on /create-password. */
export function loginResultRequiresPasswordChange(
  result?: PasswordGateLoginResult | null
): boolean {
  if (result?.mustChangePassword === true) return true
  return Boolean(isSelectiveAiDomains() && result?.isDefaultPassword)
}

/**
 * Only login/handoff responses may set mustCreatePassword=true.
 * USER_ME refreshes must not re-lock users who already set a password.
 */
export function resolveMustCreatePasswordFromLoginInfo(
  currentMustCreatePassword: boolean,
  action: {
    payload?: {result?: PasswordGateLoginResult}
    requestParams?: {authToken?: string; username?: string; idToken?: string}
  }
): boolean {
  const result = action?.payload?.result
  const requestParams = action?.requestParams ?? {}
  const isLoginFlow = Boolean(
    requestParams.authToken || requestParams.username || requestParams.idToken
  )

  // Generated password (any domain): the login response is authoritative.
  if (result?.mustChangePassword === true && isLoginFlow) {
    return true
  }

  if (isSelectiveAiDomains()) {
    const incomingDefaultPassword = result?.isDefaultPassword
    if (incomingDefaultPassword === false) {
      return result?.mustChangePassword === true ? currentMustCreatePassword : false
    }
    if (incomingDefaultPassword === true && isLoginFlow) {
      return true
    }
    return currentMustCreatePassword
  }

  if (result?.mustChangePassword === false) {
    return false
  }
  return currentMustCreatePassword
}

export function clearMustCreatePassword(dispatch: Dispatch): void {
  clearMustCreatePasswordStorage()
  dispatch({type: REDUX_SET_MUST_CREATE_PASSWORD, payload: false})
}

export function setMustCreatePassword(dispatch: Dispatch): void {
  markMustCreatePasswordStorage()
  dispatch({type: REDUX_SET_MUST_CREATE_PASSWORD, payload: true})
}

export function syncMustCreatePasswordFromLogin(
  dispatch: Dispatch,
  result?: PasswordGateLoginResult
): void {
  if (loginResultRequiresPasswordChange(result)) {
    setMustCreatePassword(dispatch)
  } else if (isSelectiveAiDomains() || result?.mustChangePassword === false) {
    clearMustCreatePassword(dispatch)
  }
}

export function markMustCreatePasswordStorage(): void {
  try {
    localStorage.setItem(STORAGE_KEY, 'true')
  } catch {
    // ignore storage errors
  }
}

export function clearMustCreatePasswordStorage(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore storage errors
  }
}

export function isPasswordAlreadySetError(err: {message?: string; statusCode?: number}): boolean {
  const lower = (err?.message ?? '').toLowerCase()
  return (
    err?.statusCode === 409 ||
    (err?.statusCode === 400 &&
      (lower.includes('already set') ||
        lower.includes('already created') ||
        lower.includes('not default') ||
        lower.includes('please sign in') ||
        lower.includes('already has a password')))
  )
}
